// ./src/utils/api/CrewSessionService.ts
//
// The NON-ACCOUNT crew member's session token (docs/CREW-ACCESS.md, scenario 3),
// stored in localStorage and sent on every request as the `x-crew-token` header
// (injected by the Apollo link in config/apollo.ts). This is a separate credential
// from the account JWT (which lives in HTTP-only cookies) — a person with no
// account uses ONLY this. Account users reaching a cross-account event don't need
// it (their cookie JWT authorizes them server-side).
const CREW_TOKEN_KEY = "eventsmagic_crew_token";
const CREW_EVENT_KEY = "eventsmagic_crew_event";
// The role the session signed in with — a HINT for the app (where to land, which pages to keep a voucher redeemer off);
// the server reads the live role from the crew row on every request.
const CREW_ROLE_KEY = "eventsmagic_crew_role";

export interface CrewSessionInfo {
  token: string;
  eventId: string;
  eventTitle?: string | null;
  guestRole?: string | null;
}

export class CrewSessionService {
  /** The raw crew token, or null. Safe on SSR / disabled storage. */
  static getToken(): string | null {
    try {
      return localStorage.getItem(CREW_TOKEN_KEY);
    } catch {
      return null;
    }
  }

  /** True when a crew session token is present. */
  static hasSession(): boolean {
    return !!CrewSessionService.getToken();
  }

  /** The event this crew session belongs to, or null. */
  static getEventId(): string | null {
    try {
      return localStorage.getItem(CREW_EVENT_KEY);
    } catch {
      return null;
    }
  }

  /** The role the session signed in with (a hint — see CREW_ROLE_KEY), or null. */
  static getRole(): string | null {
    try {
      return localStorage.getItem(CREW_ROLE_KEY);
    } catch {
      return null;
    }
  }

  /** Persist a crew session after a successful crewSignIn. */
  static store(info: CrewSessionInfo): void {
    try {
      localStorage.setItem(CREW_TOKEN_KEY, info.token);
      if (info.eventId) {
        localStorage.setItem(CREW_EVENT_KEY, info.eventId);
      }
      if (info.guestRole) {
        localStorage.setItem(CREW_ROLE_KEY, info.guestRole);
      } else {
        localStorage.removeItem(CREW_ROLE_KEY);
      }
    } catch {
      // storage disabled — the session simply won't persist across reloads
    }
  }

  /** Clear the crew session (sign out of crew mode). */
  static clear(): void {
    try {
      localStorage.removeItem(CREW_TOKEN_KEY);
      localStorage.removeItem(CREW_EVENT_KEY);
      localStorage.removeItem(CREW_ROLE_KEY);
    } catch {
      // no-op
    }
  }
}
